import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Rocket, ChevronDown, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingChecklistProps {
  onPageChange: (page: string) => void;
}

const STEPS = [
  { key: 'demo_account', label: 'Demo account created', auto: true },
  { key: 'portfolio', label: 'Sample portfolio loaded', auto: true },
  { key: 'strategy', label: 'First strategy deployed', auto: true },
  { key: 'signal_reviewed', label: 'Review your first AI signal', link: 'signals', linkLabel: 'Go to Signals' },
  { key: 'marketplace_visited', label: 'Explore the marketplace', link: 'marketplace', linkLabel: 'Browse Strategies' },
];

const OnboardingChecklist = ({ onPageChange }: OnboardingChecklistProps) => {
  const { user, updateUserMetadata } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const completedSteps: string[] = user?.user_metadata?.onboarding_completed_steps || [];
  const completedCount = STEPS.filter((s) => completedSteps.includes(s.key)).length;
  const allDone = completedCount === STEPS.length;

  if (dismissed || !user?.user_metadata?.onboarding_auto_setup) return null;
  if (allDone) {
    // Auto-dismiss after showing completion message briefly
    setTimeout(() => setDismissed(true), 4000);
  }

  const progress = (completedCount / STEPS.length) * 100;

  const handleDismiss = async () => {
    setDismissed(true);
    await updateUserMetadata({ onboarding_dismissed: true });
  };

  if (user?.user_metadata?.onboarding_dismissed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="rounded-xl bg-[hsl(var(--background-secondary))] border border-[hsl(var(--card-border))] border-l-[3px] border-l-[hsl(var(--accent-purple))] overflow-hidden"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[hsl(var(--background-tertiary))] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-[hsl(var(--accent-purple))]" />
          <span className="text-xs font-semibold font-heading">Getting Started</span>
          <span className="text-[10px] text-muted-foreground font-mono-data">{completedCount}/{STEPS.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-16">
            <Progress value={progress} className="h-1" />
          </div>
          <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Steps */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-2.5 space-y-1.5">
              {allDone ? (
                <div className="flex items-center gap-2 py-2">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent-green))]" />
                  <span className="text-xs text-[hsl(var(--accent-green))] font-medium">You're all set! This checklist will disappear.</span>
                </div>
              ) : (
                STEPS.map((step) => {
                  const done = completedSteps.includes(step.key);
                  return (
                    <div key={step.key} className="flex items-center gap-2 py-0.5">
                      {done ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--accent-green))] flex-shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
                      )}
                      <span className={`text-[11px] flex-1 ${done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                        {step.label}
                      </span>
                      {!done && step.link && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPageChange(step.link!);
                          }}
                          className="text-[10px] text-[hsl(var(--accent-blue))] hover:underline whitespace-nowrap"
                        >
                          {step.linkLabel} →
                        </button>
                      )}
                    </div>
                  );
                })
              )}
              {!allDone && (
                <button
                  onClick={handleDismiss}
                  className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground mt-1"
                >
                  Dismiss
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OnboardingChecklist;
